import { ApiMethod } from '../servicelayer/apiMethod';
import RequestConfig, { requestConfigConverter } from '../servicelayer/requestConfig';

export type ExtendedFetchFunctionReturn<T> = {
  /** The payload returned from the server */
  data: T,
  /** The HTTP code returned from the server */
  status: number,
  /** The HTTP status message returned by the server */
  statusText: string,
  /** Headers sent by server  */
  headers: Record<string, string>,
  /** The original request configuration */
  requestConfig: RequestConfig,
};

export type ExtendedFetchFunc = {
  <T = any>(method: ApiMethod, url: string, requestConfig: RequestConfig): Promise<ExtendedFetchFunctionReturn<T>>;
  get: <T = any>(url: string, requestConfig: RequestConfig) => Promise<ExtendedFetchFunctionReturn<T>>;
  put: <T = any>(url: string, requestConfig: RequestConfig) => Promise<ExtendedFetchFunctionReturn<T>>;
  post: <T = any>(url: string, requestConfig: RequestConfig) => Promise<ExtendedFetchFunctionReturn<T>>;
  delete: <T = any>(url: string, requestConfig: RequestConfig) => Promise<ExtendedFetchFunctionReturn<T>>;
  patch: <T = any>(url: string, requestConfig: RequestConfig) => Promise<ExtendedFetchFunctionReturn<T>>;
  link: <T = any>(url: string, requestConfig: RequestConfig) => Promise<ExtendedFetchFunctionReturn<T>>;
  options: <T = any>(url: string, requestConfig: RequestConfig) => Promise<ExtendedFetchFunctionReturn<T>>;
  head: <T = any>(url: string, requestConfig: RequestConfig) => Promise<ExtendedFetchFunctionReturn<T>>;
  defaults: {
    baseUrl?: string,
    headers: Record<string, string>,
  };
};

const extendedFetch = <ExtendedFetchFunc>(async <T>(method: ApiMethod, url: string, requestConfig: RequestConfig): Promise<ExtendedFetchFunctionReturn<T>> => {
  const fullUrl = requestConfigConverter.getFullUrl(url, requestConfig);
  const resp = await fetch(fullUrl, requestConfigConverter.toFetch(method, fullUrl, requestConfig));
  const headers = {} as Record<string, string>;
  resp.headers.forEach((value: string, key: string) => { headers[key] = value; });

  let payload: T;
  let { responseType } = requestConfig;
  if (!responseType) {
    const contentType = (resp.headers.has('content-type') ? resp.headers.get('content-type') : undefined);
    if (contentType?.includes('json')) {
      responseType = 'json';
    }
  }
  try {
    switch (responseType) {
      case 'arraybuffer':
        payload = await resp.arrayBuffer() as unknown as T;
        break;
      case 'formdata':
        payload = await resp.formData() as unknown as T;
        break;
      case 'blob':
        payload = await resp.blob() as unknown as T;
        break;
      case 'json':
        payload = await resp.json() as unknown as T;
        break;
      case 'text':
        payload = await resp.text() as unknown as T;
        break;
      default:
        payload = await resp.text() as unknown as T;
        break;
    }
  } catch (error: any) {
    throw new Error(`Unable to parse response as '${responseType}': ${error?.message ?? error}`);
  }

  const result = {
    data: payload,
    status: resp.status,
    statusText: resp.statusText,
    headers,
    requestConfig,
  } as ExtendedFetchFunctionReturn<T>;

  if (resp.ok) {
    return result;
  }
  // Throw result to mimic axios behaviour which threw any non-ok responses
  throw result;
});

extendedFetch.defaults = {
  headers: {},
};

extendedFetch.get = (url: string, requestConfig: RequestConfig) => extendedFetch('get', url, requestConfig);
extendedFetch.put = (url: string, requestConfig: RequestConfig) => extendedFetch('put', url, requestConfig);
extendedFetch.post = (url: string, requestConfig: RequestConfig) => extendedFetch('post', url, requestConfig);
extendedFetch.delete = (url: string, requestConfig: RequestConfig) => extendedFetch('delete', url, requestConfig);
extendedFetch.patch = (url: string, requestConfig: RequestConfig) => extendedFetch('patch', url, requestConfig);
extendedFetch.link = (url: string, requestConfig: RequestConfig) => extendedFetch('link', url, requestConfig);
extendedFetch.options = (url: string, requestConfig: RequestConfig) => extendedFetch('options', url, requestConfig);
extendedFetch.head = (url: string, requestConfig: RequestConfig) => extendedFetch('head', url, requestConfig);

export default extendedFetch;
